import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Menu, UtensilsCrossed, PawPrint } from "lucide-react";
import { CUISINE_ORDER, metaFor } from "@/lib/cuisines";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

export const Navbar = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const submitSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/cuisine/all?q=${encodeURIComponent(query.trim())}`);
      setQuery("");
      setOpen(false);
    }
  };

  return (
    <header className="sticky top-0 z-50 border-b border-gray-100 bg-white/80 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link
            to="/"
            data-testid="nav-brand-link"
            className="flex items-center gap-2 group"
          >
            <UtensilsCrossed className="h-5 w-5 text-gray-900" />
            <span className="font-heading text-2xl font-semibold tracking-tight text-gray-900">
              Pan &amp; Pantry
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {CUISINE_ORDER.map((key) => {
              const meta = metaFor(key);
              return (
                <Link
                  key={key}
                  to={`/cuisine/${key}`}
                  data-testid={`nav-${key}-link`}
                  className="group flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
                  {meta.name}
                </Link>
              );
            })}
            <Link
              to="/cuisine/pet"
              data-testid="nav-pet-link"
              className="flex items-center gap-1.5 text-sm font-medium text-sky-700 hover:text-sky-900 transition-colors"
            >
              <PawPrint className="h-4 w-4" />
              Pet-Friendly
            </Link>
          </nav>

          <div className="flex items-center gap-3">
            <form
              onSubmit={submitSearch}
              className="hidden md:flex items-center border border-gray-200 focus-within:border-gray-900 transition-colors"
            >
              <Search className="h-4 w-4 text-gray-400 ml-3" />
              <input
                data-testid="nav-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search recipes..."
                className="w-40 lg:w-56 px-2 py-2 text-sm outline-none bg-transparent placeholder:text-gray-400"
              />
            </form>

            <Link
              to="/admin"
              data-testid="nav-admin-link"
              className="hidden md:inline-flex text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors"
            >
              Admin
            </Link>

            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button
                  data-testid="nav-mobile-menu-button"
                  className="lg:hidden p-2 text-gray-900"
                  aria-label="Open menu"
                >
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80">
                <SheetHeader>
                  <SheetTitle className="font-heading text-2xl">Menu</SheetTitle>
                </SheetHeader>
                <form onSubmit={submitSearch} className="mt-6 flex items-center border border-gray-200">
                  <Search className="h-4 w-4 text-gray-400 ml-3" />
                  <input
                    data-testid="mobile-search-input"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search recipes..."
                    className="flex-1 px-2 py-2.5 text-sm outline-none bg-transparent"
                  />
                </form>
                <div className="mt-6 flex flex-col gap-1">
                  {CUISINE_ORDER.map((key) => {
                    const meta = metaFor(key);
                    return (
                      <Link
                        key={key}
                        to={`/cuisine/${key}`}
                        onClick={() => setOpen(false)}
                        data-testid={`mobile-nav-${key}-link`}
                        className="flex items-center gap-3 py-3 text-base font-medium text-gray-800 border-b border-gray-100"
                      >
                        <span className={`h-2 w-2 rounded-full ${meta.dot}`} />
                        {meta.name}
                      </Link>
                    );
                  })}
                  <Link
                    to="/cuisine/pet"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-nav-pet-link"
                    className="flex items-center gap-3 py-3 text-base font-medium text-sky-700 border-b border-gray-100"
                  >
                    <PawPrint className="h-4 w-4" />
                    Pet-Friendly
                  </Link>
                  <Link
                    to="/admin"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-nav-admin-link"
                    className="py-3 text-base font-medium text-gray-500"
                  >
                    Admin
                  </Link>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};
